import { OcpiAdminRepository } from '../data/ocpi-admin-repository';
import type { OcpiPartyDto } from '../types/api-types';

/**
 * Use case for onboarding an operator to OCPI
 * Creates or updates the CPO party configuration for an operator
 *
 * This is used when an operator is not yet set up in OCPI and needs to be
 * configured with their party identification (partyId, countryCode) and name.
 */
export class OnboardOperatorUseCase {
  constructor(private readonly repository: OcpiAdminRepository) {}

  /**
   * Onboard an operator to OCPI by creating/updating their CPO party
   *
   * @param operatorId - Nayax operator ID
   * @param partyId - OCPI Party ID (exactly 3 uppercase letters, e.g., "ABC")
   * @param countryCode - ISO 3166-1 alpha-2 country code (exactly 2 uppercase letters, e.g., "US", "IL")
   * @param name - Display name for the operator in OCPI network
   * @throws Error if validation fails or API call fails
   */
  async execute(
    operatorId: number,
    partyId: string,
    countryCode: string,
    name: string
  ): Promise<void> {
    // Validate inputs
    this.validatePartyId(partyId);
    this.validateCountryCode(countryCode);
    this.validateName(name);

    const partyData: OcpiPartyDto = {
      ocpi_id: {
        partyId: partyId.toUpperCase(),
        countryCode: countryCode.toUpperCase(),
      },
      name: name.trim(),
    };

    await this.repository.updateCpoParty(operatorId, partyData);
  }

  /**
   * Validates Party ID format
   * Must be exactly 3 uppercase letters (A-Z)
   */
  private validatePartyId(partyId: string): void {
    if (!partyId || partyId.trim().length === 0) {
      throw new Error('Party ID is required');
    }

    const trimmed = partyId.trim().toUpperCase();
    if (trimmed.length !== 3) {
      throw new Error('Party ID must be exactly 3 characters');
    }

    if (!/^[A-Z]{3}$/.test(trimmed)) {
      throw new Error('Party ID must contain only letters (A-Z)');
    }
  }

  /**
   * Validates Country Code format
   * Must be exactly 2 uppercase letters (ISO 3166-1 alpha-2)
   */
  private validateCountryCode(countryCode: string): void {
    if (!countryCode || countryCode.trim().length === 0) {
      throw new Error('Country code is required');
    }

    const trimmed = countryCode.trim().toUpperCase();
    if (trimmed.length !== 2) {
      throw new Error('Country code must be exactly 2 characters');
    }

    if (!/^[A-Z]{2}$/.test(trimmed)) {
      throw new Error('Country code must contain only letters (A-Z)');
    }
  }

  /**
   * Validates operator name
   * Must be non-empty string
   */
  private validateName(name: string): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Operator name is required');
    }

    if (name.trim().length < 2) {
      throw new Error('Operator name must be at least 2 characters');
    }

    if (name.trim().length > 100) {
      throw new Error('Operator name must be less than 100 characters');
    }
  }
}
